import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  MessagesSquare, Send, Loader2, User, ArrowLeft, ExternalLink, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const now = new Date();
  const diff = now - dt;
  if (diff < 86400000 && dt.getDate() === now.getDate()) {
    return dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  if (diff < 604800000) {
    return dt.toLocaleDateString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  }
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export const PortalChat = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const messagesEndRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const [convRes, unreadRes] = await Promise.all([
        api.get('/portal-chat/conversations'),
        api.get('/portal-chat/unread-total'),
      ]);
      setConversations(convRes.data || []);
      setUnreadTotal(unreadRes.data?.count || 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Poll for new messages every 15 seconds
  useEffect(() => {
    const interval = setInterval(fetchConversations, 15000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const selectConversation = async (customerId) => {
    setSelectedCustomerId(customerId);
    setMsgLoading(true);
    try {
      const res = await api.get(`/customers/${customerId}/portal-messages`);
      setMessages(res.data || []);
      // Mark as read
      await api.post(`/customers/${customerId}/portal-messages/read`).catch(() => {});
      fetchConversations();
    } catch (err) {
      toast.error('Failed to load messages');
    }
    setMsgLoading(false);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll selected conversation for new messages
  useEffect(() => {
    if (!selectedCustomerId) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/customers/${selectedCustomerId}/portal-messages`);
        setMessages(res.data || []);
      } catch { /* ignore */ }
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedCustomerId]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedCustomerId) return;
    setSending(true);
    try {
      await api.post(`/customers/${selectedCustomerId}/portal-messages`, { message: newMessage });
      setNewMessage('');
      const res = await api.get(`/customers/${selectedCustomerId}/portal-messages`);
      setMessages(res.data || []);
    } catch {
      toast.error('Failed to send message');
    }
    setSending(false);
  };

  const selectedConv = conversations.find(c => c.customer_id === selectedCustomerId);

  return (
    <Layout>
      <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="portal-chat-page">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <MessagesSquare className="w-5 h-5 text-blue-600" />
            <h1 className="text-lg font-semibold text-slate-900">Portal Chat</h1>
            {unreadTotal > 0 && (
              <Badge variant="destructive" className="text-xs">{unreadTotal} unread</Badge>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={fetchConversations}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Main area */}
        <div className="flex flex-1 overflow-hidden">
          {/* Conversation list */}
          <div className={`w-80 border-r bg-white flex-shrink-0 flex flex-col ${selectedCustomerId ? 'hidden md:flex' : 'flex'}`}>
            <div className="px-4 py-3 border-b">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{conversations.length} Conversations</p>
            </div>
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">
                  No portal messages yet
                </div>
              ) : (
                conversations.map(conv => (
                  <button
                    key={conv.customer_id}
                    onClick={() => selectConversation(conv.customer_id)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                      selectedCustomerId === conv.customer_id ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                    }`}
                    data-testid={`chat-conv-${conv.customer_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative">
                        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-slate-500" />
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-blue-500 rounded-full border-2 border-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm truncate ${conv.unread_count > 0 ? 'font-semibold' : 'font-medium text-slate-600'}`}>
                            {conv.customer_name}
                          </span>
                          {conv.unread_count > 0 && (
                            <Badge variant="destructive" className="ml-1 h-5 min-w-[20px] text-[10px]">{conv.unread_count}</Badge>
                          )}
                        </div>
                        <p className={`text-xs truncate mt-0.5 ${conv.unread_count > 0 ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>
                          {conv.last_is_customer ? '' : 'You: '}{conv.last_message}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{formatTime(conv.last_message_time)}</p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </ScrollArea>
          </div>

          {/* Chat thread */}
          <div className={`flex-1 flex flex-col bg-slate-50 ${!selectedCustomerId ? 'hidden md:flex' : 'flex'}`}>
            {!selectedCustomerId ? (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <MessagesSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Select a conversation to view messages</p>
                </div>
              </div>
            ) : (
              <>
                {/* Thread header */}
                <div className="flex items-center gap-3 px-4 py-3 bg-white border-b flex-shrink-0">
                  <Button variant="ghost" size="icon" className="md:hidden h-8 w-8" onClick={() => setSelectedCustomerId(null)}>
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <User className="w-8 h-8 p-1.5 bg-slate-100 rounded-full text-slate-500" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900">{selectedConv?.customer_name}</p>
                    <p className="text-xs text-slate-500">{selectedConv?.customer_email}</p>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <a href={`/customers/${selectedCustomerId}`}>
                      <ExternalLink className="w-3 h-3 mr-1" /> View Customer
                    </a>
                  </Button>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 px-4 py-4">
                  {msgLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-8 text-sm text-slate-400">No messages yet</div>
                  ) : (
                    <div className="space-y-3 max-w-2xl mx-auto">
                      {messages.map(msg => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.is_customer ? 'justify-start' : 'justify-end'}`}
                          data-testid={`chat-msg-${msg.id}`}
                        >
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                            msg.is_customer
                              ? 'bg-white border border-slate-200 rounded-bl-md'
                              : 'bg-blue-600 text-white rounded-br-md'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                            <div className={`flex items-center gap-2 mt-1 ${msg.is_customer ? 'text-slate-400' : 'text-blue-200'}`}>
                              <span className="text-[10px]">{msg.sender_name}</span>
                              <span className="text-[10px]">{formatTime(msg.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Input */}
                <div className="px-4 py-3 bg-white border-t flex-shrink-0">
                  <div className="flex items-center gap-2 max-w-2xl mx-auto">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                      placeholder="Type a message..."
                      className="flex-1"
                      data-testid="chat-input"
                    />
                    <Button
                      onClick={handleSend}
                      disabled={sending || !newMessage.trim()}
                      size="icon"
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="chat-send-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PortalChat;
